import axios from "axios";
import store from "@/store";
import customModal from "../customModal/customModal.vue";
import VPagination from "@hennge/vue3-pagination";

export default {
  name: "viewLogModal",
  components: { customModal, VPagination },
  props: {
    reportName: { type: String, default: "" },
    programId: { type: String, default: "" },
    sourceId: { type: String, default: "" },
  },
  emits: ["closeModal"],
  data() {
    return {
      copied: false,
      logLoader: false,
      historyLoader: false,
      logData: null,
      activeTab: "execution",
      searchQuery: "",
      levelFilter: "all",
      baseUrl: process.env.VUE_APP_Service_URL,
      currentLogId: "",
      rawHistoryData: [],
      historyPage: 1,
      historyPageSize: 10,
      historyTotalCount: 0,
      lastFetchKey: "",
    };
  },
  computed: {
    fetchKey() {
      return `${this.programId || ""}::${this.sourceId || ""}`;
    },
    parsedLines() {
      if (!this.logData?.executionLog) return [];
      return this.logData.executionLog.map((line, i) => ({
        lineNum: i + 1,
        time: line.time || "",
        message: line.message || line,
        level: line.level || this.detectLevel(line.message || line),
      }));
    },
    filteredLines() {
      return this.parsedLines.filter((line) => {
        const matchLevel =
          this.levelFilter === "all" || line.level === this.levelFilter;
        const matchSearch =
          !this.searchQuery ||
          line.message.toLowerCase().includes(this.searchQuery.toLowerCase());
        return matchLevel && matchSearch;
      });
    },
    successCount() {
      return this.parsedLines.filter((l) => l.level === "success").length;
    },
    warningCount() {
      return this.parsedLines.filter((l) => l.level === "warning").length;
    },
    errorCount() {
      return this.parsedLines.filter((l) => l.level === "error").length;
    },
    infoCount() {
      return this.parsedLines.filter((l) => l.level === "info").length;
    },
    historySuccessCount() {
      if (!this.logData?.runHistory) return 0;
      return this.logData.runHistory.filter((h) => h.status === "Success")
        .length;
    },
    historyFailedCount() {
      if (!this.logData?.runHistory) return 0;
      return this.logData.runHistory.filter(
        (h) => h.status !== "Success" && h.status !== ""
      ).length;
    },
    totalHistoryPages() {
      return Math.ceil(this.historyTotalCount / this.historyPageSize) || 1;
    },
    paginatedHistory() {
      return this.logData?.runHistory || [];
    },
  },
  watch: {
    fetchKey: {
      immediate: true,
      async handler(newVal) {
        if (!this.programId || !this.sourceId || newVal === this.lastFetchKey) {
          return;
        }

        this.lastFetchKey = newVal;
        if (this.programId && this.sourceId) {
          this.resetState();
          await this.fetchLog();
        }
      },
    },
  },

  methods: {
    resetState() {
      this.logData = null;
      this.activeTab = "execution";
      this.searchQuery = "";
      this.levelFilter = "all";
      this.currentLogId = "";
      this.rawHistoryData = [];
      this.historyPage = 1;
      this.historyTotalCount = 0;
    },

    detectLevel(msg) {
      if (!msg) return "default";
      const m = msg.toLowerCase();
      if (m.includes("error") || m.includes("failed")) return "error";
      if (m.includes("warn")) return "warning";
      if (m.includes("info")) return "info";
      if (
        m.includes("success") ||
        m.includes("retrieved") ||
        m.includes("records")
      ) {
        return "success";
      }
      return "default";
    },

    getLineClass(level) {
      return `log-line-${level || "default"}`;
    },

    mapHistoryEntry(entry) {
      return {
        id: entry._id || entry.Id || "",
        runAt:
          entry.CreatedAt || entry.runAt || entry.RunAt || entry.created_at || "",
        RunByName:
          entry.RunByName ||
          entry.runbyname ||
          entry.UserName ||
          entry.runBy ||
          entry.RunBy ||
          entry.DeveloperName ||
          entry.developer_name ||
          "",
        status: entry.Status || entry.status || "",
        duration: entry.DurationSeconds
          ? `${entry.DurationSeconds}s`
          : entry.duration || entry.Duration || "",
        version: entry.Version || entry.version || "",
        sourceId: entry.SourceId || entry.sourceId || "",
        reportType: entry.ReportType || "",
        programType: entry.ProgramType || "",
      };
    },

    getHistoryRows(payload) {
      if (Array.isArray(payload)) return payload;

      return (
        payload?.runHistory ||
        payload?.RunHistory ||
        payload?.history ||
        payload?.data ||
        payload?.Data ||
        payload?.result?.runHistory ||
        payload?.result?.data ||
        payload?.result?.Data ||
        []
      );
    },

    getHistoryTotal(payload, rows) {
      const total =
        payload?.Total ||
        payload?.total ||
        payload?.total_count ||
        payload?.totalCount ||
        payload?.TotalCount ||
        payload?.count ||
        payload?.Count ||
        payload?.result?.Total ||
        payload?.result?.total ||
        payload?.result?.total_count ||
        payload?.result?.totalCount ||
        payload?.result?.TotalCount ||
        rows.length;

      if (total === undefined) {
        console.warn("Missing total count from backend!");
        return 0;
      }

      return total;
    },

    async fetchHistory(page = this.historyPage) {
      if (!this.sourceId || !this.logData) return;

      this.historyLoader = true;
      const idtoken = store.getters.getIdToken;

      try {
        const historyRes = await axios.get(
          `${this.baseUrl}/stats-python/execution-log/history/${this.sourceId}`,
          {
            params: {
              PageNumber: page,
              PageSize: this.historyPageSize,
            },
            headers: {
              Authorization: "Bearer " + idtoken,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Run history API response:", historyRes.data);

        const historyData = this.getHistoryRows(historyRes.data);

        this.historyPage = page;
        this.historyTotalCount = this.getHistoryTotal(
          historyRes.data,
          historyData
        );
        this.rawHistoryData = historyData;
        this.logData.runHistory = historyData.map((entry) =>
          this.mapHistoryEntry(entry)
        );

        console.log("Mapped run history:", this.logData.runHistory);
      } catch (historyErr) {
        console.error("History API Error:", historyErr);
        this.historyTotalCount = 0;
        this.rawHistoryData = [];
        this.logData.runHistory = [];
      } finally {
        this.historyLoader = false;
      }
    },

    async onHistoryPageChange(page) {
      if (this.historyLoader) return;
      await this.fetchHistory(page);
    },

    async fetchLog() {
      this.logLoader = true;
      this.logData = null;
      const idtoken = store.getters.getIdToken;

      try {
        const res = await axios.get(
          `${this.baseUrl}/stats-python/execution-log/by-program/${this.programId}`,
          {
            headers: {
              Authorization: "Bearer " + idtoken,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Execution log API response:", res.data);

        this.currentLogId = res.data._id || "";

        this.logData = {
          FullTitle: res.data.FullTitle || "",
          Version: res.data.Version || "",
          RunByName: res.data.RunByName || res.data.UserName || res.data.runBy || "",
          ProgramType: res.data.ProgramType || "",
          status: res.data.Status || "",
          Duration: res.data.DurationSeconds
            ? `${res.data.DurationSeconds}s`
            : res.data.duration || res.data.Duration || "",
          CreatedAt: res.data.CreatedAt || res.data.runAt || "",
          executionLog:
            res.data.ExecutionLog || res.data.executionLog || res.data.logs || [],
          runHistory: [],
        };

        if (this.sourceId) {
          await this.fetchHistory(1);
        }
      } catch (err) {
        console.error("Execution log API Error:", err);
        this.logData = null;
      } finally {
        this.logLoader = false;
      }
    },

    viewHistoryLog(id) {
      if (!id) {
        console.warn("No id provided for viewHistoryLog");
        return;
      }

      if (id === this.currentLogId) {
        this.activeTab = "execution";
        return;
      }

      const historyEntry = this.rawHistoryData.find((h) => (h._id || h.Id) === id);

      if (!historyEntry) {
        console.warn("No history entry found for id:", id);
        return;
      }

      console.log("Viewing history log for id:", id, historyEntry);

      this.currentLogId = historyEntry._id || historyEntry.Id || id;

      this.logData = {
        ...this.logData,
        FullTitle: historyEntry.FullTitle || this.logData.FullTitle,
        Version: historyEntry.Version || this.logData.Version,
        RunByName: historyEntry.RunByName || historyEntry.UserName || historyEntry.runBy || this.logData.RunByName,
        ProgramType: historyEntry.ProgramType || this.logData.ProgramType,
        status: historyEntry.Status || historyEntry.status || this.logData.status,
        Duration: historyEntry.DurationSeconds
          ? `${historyEntry.DurationSeconds}s`
          : historyEntry.duration || historyEntry.Duration || this.logData.Duration,
        CreatedAt:
          historyEntry.CreatedAt || historyEntry.runAt || this.logData.CreatedAt,
        executionLog:
          historyEntry.ExecutionLog ||
          historyEntry.executionLog ||
          historyEntry.logs ||
          [],
        runHistory: this.logData.runHistory,
      };

      this.activeTab = "execution";
    },

    copyLog() {
      const text = this.parsedLines.map((l) => `${l.time} ${l.message}`).join("\n");
      if (text) {
        navigator.clipboard.writeText(text).then(() => {
          this.copied = true;
          setTimeout(() => {
            this.copied = false;
          }, 2000);
        });
      }
    },

    downloadLog() {
      const text = this.parsedLines
        .map((l) => `${l.lineNum}\t${l.time}\t${l.message}`)
        .join("\n");
      const blob = new Blob([text], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `log_${this.programId}.txt`;
      link.click();
    },

    closeModal() {
      this.$emit("closeModal");
    },

    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return Number.isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
    },
  },
};
